import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { stat } from 'fs';

interface PrivateItem {
  id: number;
  count: number;
}

interface CountState {
  private: PrivateItem[];
  commonCount: number;
  newBadgeCount: number;
  micState: string;
  speaker: string;
  audioOutput: string;
  isTrainerPresent: boolean;
  seatNumber: number | null;
  showPopupToSwapSeat: boolean;
  raisedByEmail: string | null;
  makeGroupLeader: string;
  isTimerStarted: boolean;
  countDownTime: number;
  readyToSwapSeat: boolean;
  swapAcceptedByEmail: string | null;
  isActivityMode: boolean;
  isAnnouncementEnabled: boolean;
  raiseForEmail: string | null;
  MoveToAnotherGroup: boolean;
  MoveToAnotherGroupRequestBy: object;
  MoveToAnotherGroupName: string | null;
  removeEmailFromAnotherTable: string | null;
  leaveFromGroupEmail: string | null;
  TrainerName: string;
  isTrainerMute: boolean;
  csGroupName: string;
  csSelectedTraineeForPresentation: object;
  rpSelectedTraineeForPresentation: object;
  gdSelectedTraineeForPresentation: object;
  tableSelectedExpanded: any[];
  badgeCount: number;
  sessionPlanPanelOpen: boolean;
  participantPanelOpen: boolean;
  sessionPanelWidth: number;
  participantPanelWidth: number;
  response: any;
}

const initialState: CountState = {
  private: [],
  commonCount: 0,
  newBadgeCount: 0,
  micState: 'on',
  speaker: 'off',
  audioOutput: 'off',
  isTrainerPresent: false,
  seatNumber: null,
  showPopupToSwapSeat: false,
  raisedByEmail: null,
  makeGroupLeader: '',
  isTimerStarted: false,
  countDownTime: 0,
  readyToSwapSeat: false,
  swapAcceptedByEmail: null,
  isActivityMode: false,
  isAnnouncementEnabled: false,
  raiseForEmail: null,
  MoveToAnotherGroup: false,
  MoveToAnotherGroupRequestBy: {},
  MoveToAnotherGroupName: null,
  removeEmailFromAnotherTable: null,
  leaveFromGroupEmail: null,
  TrainerName: '',
  isTrainerMute: false,
  csGroupName: '',
  csSelectedTraineeForPresentation: {},
  rpSelectedTraineeForPresentation: {},
  gdSelectedTraineeForPresentation: {},
  tableSelectedExpanded: [],
  badgeCount: 0,
  sessionPlanPanelOpen:false,
  participantPanelOpen:false,
  sessionPanelWidth:45,
  participantPanelWidth:45,
  response: null,
};

const countSlice = createSlice({
  name: 'count',
  initialState,
  reducers: {
    addPrivateCount(state, action: PayloadAction<{ id: number; count: number }>) {
      const { id, count } = action.payload;
      const existingItem = state.private.find(item => item.id === id);
      if (existingItem) {
        existingItem.count += count;
      } else {
        state.private.push({ id, count: 1 });
      }
    },
    addCommonCount(state) {
      state.commonCount += 1;
    },
    resetPrivateCount(state, action: PayloadAction<number>) {
      const id = action.payload;
      state.private = state.private.map(item =>
        item.id === id ? { ...item, count: 0 } : item
      );
    },
    resetCommonCount(state) {
      state.commonCount = 0;
    },
    setNewBadgeCount(state) {
      state.newBadgeCount += 1;
    },
    resetNewBadgeCount(state) {
      state.newBadgeCount = 0;
    },
    setMicState(state, action: PayloadAction<{ mic: string }>) {
      state.micState = action.payload.mic;
    },
    setSpeakerState(state, action: PayloadAction<string>) {
      state.speaker = action.payload;
    },
    setTrainerInGroup(
      state,
      action: PayloadAction<{ isTrainerPresent: boolean; seatNumber: number | null; groupName: string }>
    ) {
      state.isTrainerPresent = action.payload.isTrainerPresent;
      state.seatNumber = action.payload.seatNumber;
      state.csGroupName = action.payload.groupName;
    },
    setBadgesCount(state, action: PayloadAction<{ badgeCount: number }>) {
      state.badgeCount = action.payload.badgeCount;
    },
    swapSeatRequest(
      state,
      action: PayloadAction<{ showPopupToSwapSeat: boolean; raisedByEmail: string; raiseForEmail: string }>
    ) {
      state.showPopupToSwapSeat = action.payload.showPopupToSwapSeat;
      state.raisedByEmail = action.payload.raisedByEmail;
      state.raiseForEmail = action.payload.raiseForEmail;
    },
    swapSeatAccept(
      state,
      action: PayloadAction<{ readyToSwapSeat: boolean; raisedByEmailx: string; raiseForEmailx: string }>
    ) {
      state.readyToSwapSeat = action.payload.readyToSwapSeat;
      state.raisedByEmail = action.payload.raisedByEmailx;
      state.raiseForEmail = action.payload.raiseForEmailx;
    },
    makeGroupLeader(state, action: PayloadAction<{ email: string }>) {
      state.makeGroupLeader = action.payload.email;
    },
    isTimerStarted(state, action: PayloadAction<{ isTimerStarted: boolean; countDownTime: number }>) {
      state.isTimerStarted = action.payload.isTimerStarted;
      state.countDownTime = action.payload.countDownTime;
    },
    isActivityMode(state, action: PayloadAction<{ isActivityMode: boolean }>) {
      state.isActivityMode = action.payload.isActivityMode;
    },
    updateAnnouncementStatus(state, action: PayloadAction<{ isAnnouncementEnabled: boolean }>) {
      state.isAnnouncementEnabled = action.payload.isAnnouncementEnabled;
    },
    moveToGroup(
      state,
      action: PayloadAction<{
        MoveToAnotherGroup: boolean;
        MoveToAnotherGroupName: string | null;
        MoveToAnotherGroupRequestBy: object;
      }>
    ) {
      state.MoveToAnotherGroup = action.payload.MoveToAnotherGroup;
      state.MoveToAnotherGroupName = action.payload.MoveToAnotherGroupName;
      state.MoveToAnotherGroupRequestBy = action.payload.MoveToAnotherGroupRequestBy;
    },
    leaveFromGroup(
      state,
      action: PayloadAction<{
        removeEmailFromAnotherTable: string | null;
        leaveFromGroupEmail: string | null;
      }>
    ) {
      state.removeEmailFromAnotherTable = action.payload.removeEmailFromAnotherTable;
      state.leaveFromGroupEmail = action.payload.leaveFromGroupEmail;
    },
    updateTrainerName(state, action: PayloadAction<{ TrainerName: string }>) {
      state.TrainerName = action.payload.TrainerName;
    },
    updateTrainerMicStatus(state, action: PayloadAction<{ isTrainerMute: boolean }>) {
      state.isTrainerMute = action.payload.isTrainerMute;
    },
    csCaseStudyParticipants(
      state,
      action: PayloadAction<{ csSelectedTraineeForPresentation: object }>
    ) {
      state.csSelectedTraineeForPresentation = action.payload.csSelectedTraineeForPresentation;
    },
    rpRolePlayParticipants(
      state,
      action: PayloadAction<{ rpSelectedTraineeForPresentation: object }>
    ) {
      state.rpSelectedTraineeForPresentation = action.payload.rpSelectedTraineeForPresentation;
    },
    gdGroupDiscussionParticipants(
      state,
      action: PayloadAction<{ gdSelectedTraineeForPresentation: object }>
    ) {
      state.gdSelectedTraineeForPresentation = action.payload.gdSelectedTraineeForPresentation;
    },
    tableSelectedExpanded(state, action: PayloadAction<{ tableSelectedExpanded: any[] }>) {
      console.log('Reducer payload:', action.payload.tableSelectedExpanded);
      state.tableSelectedExpanded = action.payload.tableSelectedExpanded;
    },
    updateSidePanelData(
      state,
      action: PayloadAction<{ sessionPlanPanelOpen?: boolean,participantPanelOpen?: boolean,sessionPanelWidth?: number,participantPanelWidth?: number }>
    ) {
      state.sessionPlanPanelOpen = action.payload.sessionPlanPanelOpen;
      state.participantPanelOpen = action.payload.participantPanelOpen;
      state.sessionPanelWidth = action.payload.sessionPanelWidth;
      state.participantPanelWidth = action.payload.participantPanelWidth;
    },
    addResponse(state, action: PayloadAction<any>) {
      state.response = action.payload;
    }
  },
});

export const {
  addPrivateCount,
  addCommonCount,
  resetPrivateCount,
  resetCommonCount,
  setNewBadgeCount,
  resetNewBadgeCount,
  setMicState,
  setSpeakerState,
  setTrainerInGroup,
  setBadgesCount,
  swapSeatRequest,
  swapSeatAccept,
  makeGroupLeader,
  isTimerStarted,
  isActivityMode,
  updateAnnouncementStatus,
  moveToGroup,
  leaveFromGroup,
  updateTrainerName,
  updateTrainerMicStatus,
  csCaseStudyParticipants,
  rpRolePlayParticipants,
  gdGroupDiscussionParticipants,
  tableSelectedExpanded,
  updateSidePanelData,
  addResponse
} = countSlice.actions;

export default countSlice.reducer;
